import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { getProjectIdsFromUrl } from '@dealstack/frontend/shared/dataExchange/ApiLayer/getProjectIdsFromUrl';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useServerSentEvents } from '@dealstack/frontend/shared/hooks/useServerSentEvents';
import { buildSignableDocumentsQueryKey } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/utils';
import type {
	SignableDocumentsStreamMessage,
	UseSignableDocumentsStreamArgs,
	UseSignableDocumentsStreamResult,
} from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useSignableDocumentsStream.types';

/**
 * Subscribes to the signable-documents SSE stream and invalidates the documents
 * query on each change signal and on every reconnect.
 */
export const useSignableDocumentsStream = ({
	investorId,
	investmentWorkflowId,
	excludeTsaContracts,
	enabled,
}: UseSignableDocumentsStreamArgs): UseSignableDocumentsStreamResult => {
	const queryClient = useQueryClient();
	const { mipId } = getProjectIdsFromUrl();

	const invalidate = useCallback(() => {
		void queryClient.invalidateQueries({
			queryKey: buildSignableDocumentsQueryKey({
				excludeTsaContracts,
				investmentWorkflowId,
				investorId,
			}),
		});
	}, [queryClient, excludeTsaContracts, investmentWorkflowId, investorId]);

	const onMessage = useCallback(
		(message: SignableDocumentsStreamMessage) => {
			if (message.type !== 'changed') {
				return;
			}
			logger.debug(
				`Signable-documents SSE 'changed' received for investorId: ${investorId}; invalidating query`
			);
			invalidate();
		},
		[invalidate, investorId]
	);

	return useServerSentEvents<SignableDocumentsStreamMessage>({
		// mipId is gated by `enabled` below, so the empty fallback is never connected to.
		path: formatUnicorn(
			API_PATHS.SHAREHOLDER_CONTRACT_TRANSACTION_INVESTOR_INVESTORID_PROGRAMME_MIPID_SIGNABLE_DOCUMENTS_STREAM_GET,
			{ investorId, mipId: mipId ?? '' }
		),
		enabled: Boolean(enabled && investorId && mipId),
		onMessage,
		onResync: invalidate,
		logLabel: `Signable-documents (investor ${investorId})`,
	});
};
